import React, { useRef, useEffect, useImperativeHandle, forwardRef } from "react";

export interface TurnstileWidgetHandle {
  reset: () => void;
  getToken: () => string | null;
}

interface TurnstileWidgetProps {
  sitekey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  onBeforeInteractive?: () => void;
  onAfterInteractive?: () => void;
  appearance?: "always" | "execute" | "interaction-only";
  size?: "normal" | "compact" | "flexible";
  execution?: "render" | "execute";
}

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript(): Promise<typeof window.turnstile> {
  if (typeof window === "undefined") return Promise.resolve(undefined);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  return new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`);
    if (existing) {
      const check = setInterval(() => {
        if (window.turnstile) {
          clearInterval(check);
          resolve(window.turnstile);
        }
      }, 50);
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => resolve(undefined);
    document.head.appendChild(script);
  });
}

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  (
    {
      sitekey,
      onVerify,
      onExpire,
      onError,
      onBeforeInteractive,
      onAfterInteractive,
      appearance = "interaction-only",
      size = "normal",
      execution = "render",
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const tokenRef = useRef<string | null>(null);

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          tokenRef.current = null;
          window.turnstile.reset(widgetIdRef.current);
        }
      },
      getToken: () => tokenRef.current,
    }));

    useEffect(() => {
      const el = containerRef.current;
      if (!el || !sitekey) return;

      let mounted = true;

      loadTurnstileScript().then((turnstile) => {
        if (!mounted || !el || !turnstile) return;
        if (widgetIdRef.current) return;

        const id = turnstile.render(el, {
          sitekey,
          callback: (token: string) => {
            tokenRef.current = token;
            onVerify(token);
          },
          "expired-callback": () => {
            tokenRef.current = null;
            onExpire?.();
          },
          "error-callback": () => {
            tokenRef.current = null;
            onError?.();
          },
          "before-interactive-callback": () => onBeforeInteractive?.(),
          "after-interactive-callback": () => onAfterInteractive?.(),
          appearance,
          size,
          execution,
        });

        widgetIdRef.current = id;
      });

      return () => {
        mounted = false;
        if (widgetIdRef.current && window.turnstile?.remove) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // ignore
          }
          widgetIdRef.current = null;
        }
        tokenRef.current = null;
      };
    }, [sitekey, appearance, size, execution, onVerify, onExpire, onError, onBeforeInteractive, onAfterInteractive]);

    return <div ref={containerRef} />;
  }
);

TurnstileWidget.displayName = "TurnstileWidget";

export default TurnstileWidget;
